import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api } from '../api'
import type { Product as P } from '../types'
import { Price, ProductCard } from '../components/ProductCard'
import { useBag } from '../store'
import { toast } from '../components/Toast'

export function Product() {
  const { id = '' } = useParams()
  const { add, busy } = useBag()
  const [p, setP] = useState<P | null | undefined>(undefined)
  const [related, setRelated] = useState<P[]>([])
  const [img, setImg] = useState(0)
  const [color, setColor] = useState('')
  const [size, setSize] = useState('')
  const [qty, setQty] = useState(1)
  const [added, setAdded] = useState(false)
  const [err, setErr] = useState('')

  useEffect(() => {
    setP(undefined); setImg(0); setSize(''); setAdded(false); setErr('')
    api.product(id).then(prod => { setP(prod); setColor(prod.colors[0]?.name ?? '') }).catch(() => setP(null))
    api.related(id).then(r => setRelated(r.products)).catch(() => setRelated([]))
    window.scrollTo({ top: 0 })
  }, [id])

  if (p === undefined) return <div className="container pdp"><div className="skel media" style={{ aspectRatio: '3/4' }} /><div><div className="skel line" style={{ width: '60%', height: 32 }} /><div className="skel line" style={{ width: '20%' }} /></div></div>
  if (p === null) return <div className="container notfound"><h1>We couldn't find that one.</h1><p><Link to="/women" className="link">Keep browsing</Link></p></div>

  const soldOut = p.sizes[p.sizes.length - 1]
  const submit = async () => {
    if (!size) { setErr('Please choose a size.'); return }
    setErr('')
    try { await add(p.id, color, size, qty); setAdded(true); toast('Added to your bag') } catch { setErr('Sorry — we couldn’t add that right now.') }
  }
  const dept = p.department === 'men' ? 'Men' : 'Women'
  return (
    <div className="container">
      <div className="crumbs"><Link to="/">Home</Link> / <Link to={'/' + p.department}>{dept}</Link> / {p.category}</div>
      <div className="pdp">
        <div className="gallery">
          <div className="thumbs">{p.images.map((src, i) => <button key={src} className={i === img ? 'on' : ''} onClick={() => setImg(i)} aria-label={`Image ${i + 1}`}><img src={src} alt="" /></button>)}</div>
          <div className="main"><img src={p.images[img]} alt={p.name} /></div>
        </div>
        <div className="buy">
          <h1 data-testid="product-name">{p.name}</h1>
          <Price p={p} big />
          <div className="rating"><span className="stars">{'★'.repeat(Math.round(p.rating))}{'☆'.repeat(5 - Math.round(p.rating))}</span>{p.rating.toFixed(1)} · {p.reviews} reviews</div>
          <div className="label">Color <span>{color}</span></div>
          <div className="swatches big">{p.colors.map(c => <button key={c.name} className={'swatch' + (c.name === color ? ' on' : '')} style={{ background: c.hex }} onClick={() => setColor(c.name)} aria-label={c.name} title={c.name} />)}</div>
          <div className="label">Size <span><a className="link" href="#size-guide">Size guide</a></span></div>
          <div className="pills" data-testid="sizes">
            {p.sizes.map(s => <button key={s} className={'pill' + (s === size ? ' on' : '')} data-testid={`size-${s}`} disabled={s === soldOut && p.badge === 'sale'} onClick={() => setSize(s)}>{s}</button>)}
          </div>
          {err && <div className="field"><div className="hint" data-testid="size-error">{err}</div></div>}
          <div className="label">Quantity</div>
          <div className="qty"><button onClick={() => setQty(q => Math.max(1, q - 1))} aria-label="Decrease">−</button><span data-testid="qty">{qty}</span><button onClick={() => setQty(q => Math.min(9, q + 1))} aria-label="Increase">+</button></div>
          <div className="buy-row">
            <button className="btn block" data-testid="add-to-bag" onClick={submit} disabled={busy}>{busy ? <span className="spinner" /> : null}{busy ? 'Adding' : 'Add to bag'}</button>
          </div>
          {added && <div className="added" data-testid="added"><span>Added to your bag.</span><Link to="/bag">View bag →</Link></div>}
          <p className="fit">Fits true to size. Model is 5'10" and wears a size M.</p>
          <div className="accordion">
            <details open><summary>Details</summary><div className="body"><p>{p.description}</p><ul>{p.details.map(d => <li key={d}>{d}</li>)}</ul></div></details>
            <details><summary>Fabric & care</summary><div className="body">{p.fabric}</div></details>
            <details><summary>Shipping & returns</summary><div className="body">Free standard shipping over $75 (5–7 days). Express $12 (2 days). Free returns within 30 days.</div></details>
          </div>
        </div>
      </div>
      <div className="sticky-bar"><button className="btn block" data-testid="add-to-bag-mobile" onClick={submit} disabled={busy}>Add to bag · {size || 'choose size'}</button></div>
      {related.length > 0 && (
        <section className="section" style={{ paddingTop: 0 }}>
          <div className="section-head"><h2>Complete the ensemble</h2></div>
          <div className="grid">{related.map(r => <ProductCard key={r.id} p={r} />)}</div>
        </section>
      )}
    </div>
  )
}
